<script setup>
const props = defineProps({
    headline: {
        type: String,
        required: true,
    },
    subHead: {
        type: String,
        required: false,
    },
    subText: { type: String, required: true },
    buttonText: {
        type: String,
        required: true,
    },
    pageTitle: {
        type: String,
        required: true,
    },
    container: {
        type: Boolean,
        default: false,
    },
    footer: {
        type: Boolean,
        default: false,
    },
    reducePadding: {
        type: Boolean,
        default: false
    }
});

const errorRef = ref(null);
const errorHeight = ref(0);
const emailRegex = /.+?@.+?\..{2,}/;

const submitted = ref(false);
const submittedMessage = ref('');

const config = useRuntimeConfig();
const baseURL = config.public.clientFrontEnd;
const route = useRoute();

const hubspotPortalId = '146726';
const hubspotFormId = 'bbc39d17-c359-4890-b3ba-c487a27ed366';
const hubspotCookie = useCookie('hubspotutk');

const hasError = ref(false);
const submit = async () => {
    if (!emailValue.value) {
        hasError.value = true;
        return;
    }
    const { data: responseData } = await useFetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`,
        {
            method: 'post',
            body: {
                fields: [
                    {
                        objectTypeId: '0-1',
                        name: 'email',
                        value: emailValue.value,
                    },
                ],
                context: {
                    hutk: hubspotCookie.value, // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
                    pageUri: `${baseURL}${route.fullPath}`,
                    pageName: `Inbound-2024 - ${props.pageTitle}`,
                },
            },
        }
    );
    submittedMessage.value = responseData.value.inlineMessage;
    submitted.value = true;
    setTimeout(() => { submitted.value = false; }, 10000)
};

const emailValue = ref(null);
const optOut = ref(false);
const resubbed = ref(false);
const resubFail = ref(false);

const resubEmail = async () => {
    const pageUri = `${baseURL}${route.fullPath}`;
    const pageName = `Inbound-2024 - ${props.pageTitle}`;

    const response = await $fetch(`https://forms.hsforms.com/emailcheck/v1/form-resubscribe`, {
        method: 'post',
        body: emailValue.value,
        query: {
            hs_static_app: 'forms-embed',
            hs_static_app_version: 1.5387,
            'X-HubSpot-Static-App-Info': 'forms-embed-1.5387',
            portalId: 146726,
            formId: 'bbc39d17-c359-4890-b3ba-c487a27ed366',
            resub_form_name: pageName,
            resub_form_address: pageUri,
        },
    });
    if (response.success) {
        resubbed.value = true;
    } else {
        resubFail.value = true;
    }
};

watch(emailValue, async (newValue) => {
    if (emailRegex.test(newValue)) {
        try {
            const response = await $fetch('/api/v1/subs', {
                method: 'GET',
                query: {
                    email: newValue,
                },
            });
            if (response.result == true) {
                optOut.value = true;
            } else {
                optOut.value = false;
            }
        } catch (e) {
            // console.log(e);
        }
    } else {
        optOut.value = false;
    }
});

watch(optOut, (newValue) => {
    if (newValue) {
        setTimeout(() => {
            errorHeight.value = errorRef.value.offsetHeight;
        }, 16);
    } else {
        errorHeight.value = 0;
    }
});
</script>

<template>
    <section class="block block-newsletter-signup reduced-padding" tabindex="-1" :class="{ container, 'not-footer': !footer, 'reduced-padding': reducePadding }">
        <div :class="`${container ? 'inner-container' : 'container'}`">
            <div v-if="!submitted">
                <div class="flex-container">
                    <div class="copy-container split-box">
                        <MessHtml tag="h2" :html="headline" />
                        <MessHtml tag="div" v-if="subText" :html="subText" class="subText desktop" />
                    </div>
                    <form @submit.prevent="submit" class="split-box">
                        <div class="input-wrapper" :class="`${emailValue?.length ? 'has-value' : ''}`">
                            <label for="email">Enter your email address</label>
                            <input name="email" id="email" type="email" v-model="emailValue" />
                            <span class="email-error" v-if="hasError">*Please enter a valid email address.</span>
                            <span class="email-error" v-if="optOut && !resubbed" ref="errorRef"><button class="error"
                                    @click.prevent="resubEmail">
                                    Looks like you've opted out of email communication. Click here to get an email and
                                    opt back in.
                                </button></span>
                            <span class="email-error" v-if="resubbed">Please check your email to opt back in. </span>
                            <span class="email-error" v-if="resubFail">Something went wrong. Please refresh the page and
                                try again.</span>
                        </div>
                        <button type="submit" :aria-label="buttonText">
                            <span class="h6 desktop">{{ buttonText }}</span>
                            <span class="mobile">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 22 20" fill="none">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.869 0.12191C10.6602 -0.0587822 10.3444 -0.0359825 10.1637 0.172835C9.98302 0.381653 10.0058 0.697413 10.2146 0.878105L19.8524 9.21777L0.5 9.21777C0.223858 9.21777 0 9.44163 0 9.71777C0 9.99391 0.223858 10.2178 0.5 10.2178L19.649 10.2178L10.2146 18.3814C10.0058 18.5621 9.98302 18.8779 10.1637 19.0867C10.3444 19.2955 10.6602 19.3183 10.869 19.1376L21.4198 10.0079C21.5296 9.91289 21.5926 9.77491 21.5926 9.62977C21.5926 9.48463 21.5296 9.34665 21.4198 9.25167L10.869 0.12191Z" fill="#B9CDBE"/>
                                </svg>
                            </span>
                        </button>
                    </form>
                    <MessHtml tag="div" v-if="subText" :html="subText" class="subText mobile" />
                </div>
            </div>
            <div v-else>
                <MessHtml tag="h5" :html="submittedMessage" />
            </div>
        </div>
    </section>
</template>

<style lang="scss">
.block-newsletter-signup {

    &.reduced-padding {
        margin: 0;
        padding: 0;
    }

    &.not-footer {
        margin-bottom: 5.4375rem;
        
        .container {
            
            > div {
                @include box;
                background-color: var(--teal);

                @media (min-width: $tablet)  {
                    background-image: url('@/assets/img/backgrounds/bubble-teal-11-small.png');
                    background-position: right bottom;
                    background-repeat: no-repeat;   
                    padding: 3.5rem;
                }
            }

            .flex-container {
                width: 100%;
                @media (min-width: $tablet)  {
                    justify-content: space-between;
                }
                
            }

            .flex-container form {
                margin-top: -0.65rem;
                @media (min-width: $desktop)  {
                    max-width: 406px;
                }
            }

            .copy-container {
                max-width: none;
                width: 100%;
                @media (min-width: $desktop)  {
                    width: auto;
                    flex-grow: 1;
                    flex-shrink: 2;
                }
            }
            
            .copy-container h5 {
                color: white;
                font-size: calc(20 / var(--base-fs) * 1rem);
                font-weight: 500;
                line-height: 1.75rem;
                letter-spacing: 0.025rem;
                margin-bottom: 1.125rem;
                @media (min-width: $tablet) {
                    margin-top: 0;
                }
           }

           .subText p {
                color: var(--sage);
           }

           .mess-html {
                color: white;
           }

           .input-wrapper {
                label {
                    padding: 1rem 0;
                }
           }

           button:not(.error) {
                color: white;
                padding: 1rem 0;
           }
        }
    }

    .flex-container {
        display: flex;
        gap: 2.4375rem;
        align-items: flex-start;

        h4 {
            margin-bottom: 12px;
        }

        form {
            flex-grow: 1;
            display: flex;
            color: var(--sage);
            margin-top: -1.45rem;
        }

        .copy-container {
            flex-grow: 1;
            flex-shrink: 2;
            flex-grow: 0;

            h2 {
                font-weight: 500;
                letter-spacing: 0.025rem;
                line-height: 1.625rem;
                font-size: calc(20 / var(--base-fs) * 1rem);

                @media (min-width: $tablet) {
                    margin-bottom: 1rem;
                    margin-top: -0.725rem;
                }
            }
        }

        input,
        button:not(.error) {
            border-bottom: 1.5px solid var(--sage);
        }

        .input-wrapper {
            position: relative;
            // padding: 0.86rem 1.37rem;
            flex-grow: 1;
            transition: 250ms ease;

            label {
                @include absolute-center;
                display: flex;
                align-items: center;
                padding: 0.86rem 0;
                transform-origin: top left;
                transition: 250ms ease;
                font-size: calc(18 / var(--base-fs) * 1rem);
            }

            input {
                @include absolute-center;
                padding: 0.86rem 0;
                transition: 250ms ease;
                font-size: calc(18 / var(--base-fs) * 1rem);
                color: white;
            }

            span.email-error {
                position: absolute;
                top: calc(100% + 0.25rem);
                font-weight: 900;
                color: white;
            }

            &:focus-within,
            &.has-value {
                label {
                    transform: scale(0) translateX(0) translateY(-0.25rem);
                }
            }
        }

        button:not(.error) {
            padding: 1rem 0;
            transition: 250ms ease;
            text-transform: uppercase;
            &:focus-within,
            &:hover {
                .h6 {
                    color: white;
                }
            }
        }
    }

    .subText {
        color: var(--sage);
        font-size: 0.625rem;
        line-height: 1.0078125rem;
        letter-spacing: 0.01875rem;
        font-weight: 300;

        @media (min-width: $tablet) {
            font-size: 0.6875rem;
            line-height: 1.1085625rem;
            letter-spacing: 0.020625rem;
        }
        
        a {
            color: var(--sage);
            border-bottom: 1px solid var(--sage);
            &:hover {
                color: var(--cream);
                border-color: var(--cream);
            }
        }
    }

    @media (max-width: 1060px) {
        .flex-container {
            flex-direction: column;
            gap: 30px;

            .subHead.fs-15 {
                font-size: 12px;
            }

            h4 {
                line-height: 1.2;
                margin-bottom: 1.4rem;
            }

            form {
                align-self: stretch;

                input {
                    &:focus-within {
                        // background: white;
                        // color: var(----orange);
                    }
                }
            }
        }

        .subText.fs-12 {
            font-size: 12px;
        }
    }

    @media (max-width: $mobile) {
        .inner-container {
            padding: 0 1.5rem;
        }

        .flex-container {
            font-size: 11px;

            .h6 {
                font-size: 10px;
            }
        }
    }
}
</style>
